import history from "utils/history";
import urls from "global/constants/UrlConstants";
import { store } from "utils/store";
import {
  logOutAction,
  setLastVisitedLink,
  updateAccessToken,
  updateRefreshToken,
} from "redux/authSlice";
import strings from "global/constants/StringConstants";
import { handleErrorMessage, isTruthy } from "helpers/methods";
import notifiers from "global/constants/NotificationConstants";
import { jwtDecode } from "jwt-decode";
import viewpaths from "global/constants/ViewPathConstants";
import { removeDeviceId } from "screens/Auth/AuthService";
import {
  captureApiToPostHog,
  getEventNameForEndpoint,
} from "utils/posthogApiCapture";
import {
  createFeatureBlockedError,
  createPermissionBlockedError,
  getBlockingFeature,
  getBlockingPermission,
} from "utils/apiFeatureGate";
import { IS_EMBED } from "embed/embedMode";
import { getEmbedSessionHeaders } from "embed/embedBridge";

// Request URLs carry PII in their query strings (?email=, ?loginHint=), so the per-call trace is
// kept off production. Errors still surface, but with the URL's query string stripped.
const API_TRACE_ENABLED = !urls.PROD;

const withoutQuery = (url: string) => url.split("?")[0];

/**
 * Signer-facing routes read from the shared ESIGNERS schema, which has no workspace concept. Never
 * scope those requests to a workspace — even if an admin session left an active workspace id in
 * localStorage (same-browser leak), the signer inbox / signing views must not send X-Workspace-Id.
 */
const SIGNER_ROUTE_PREFIXES = [viewpaths.inboxViewPath, viewpaths.doSign];

const onSignerRoute = (): boolean => {
  if (IS_EMBED) return true;
  const path = history?.location?.pathname ?? "";
  return SIGNER_ROUTE_PREFIXES.some((prefix) => path.startsWith(prefix));
};

export const logApiCall = (
  url: string,
  callParams?: any,
  phase: "request" | "response" | "error" = "request",
  extra?: { statusCode?: number; error?: any },
) => {
  const method = callParams?.method ?? "GET";
  if (phase === "error") {
    // eslint-disable-next-line no-console
    console.error(
      `[API ✕] ${method} ${API_TRACE_ENABLED ? url : withoutQuery(url)} (${
        extra?.statusCode ?? ""
      })`,
      extra?.error,
    );
    return;
  }
  if (!API_TRACE_ENABLED) return;
  if (phase === "request") {
    // eslint-disable-next-line no-console
    console.log(`[API →] ${method} ${url}`);
  } else {
    // eslint-disable-next-line no-console
    console.log(`[API ←] ${method} ${url} (${extra?.statusCode ?? ""})`);
  }
};

// Checks if the error code is 401 or 403 -> Logout the user
export const checkStatus = (error: any) => {
  // An embedded widget has no Inkfree login to drop and must never navigate the partner's iframe to the
  // landing page; the widget shows its own inline error instead.
  if (IS_EMBED) return false;
  if (error.status === 401) {
    const path = history.location.pathname + history.location.search;
    store.dispatch(logOutAction({}));
    store.dispatch(setLastVisitedLink(path));
    history.push(viewpaths.landingViewPath);
    return true;
  } else return false;
};

// Authenticated Call Headers For MFA
export const getMFACallParams = async (
  token: string,
  methodType: string,
  body?: any,
  signal?: any,
) => {
  return {
    method: methodType,
    headers: await getHeaderObject(token, strings.applicationJSON),
    body: JSON.stringify(body),
    signal,
  };
};

// Authenticated Call Headers
export const getCallParams = async (
  methodType: string,
  body?: any,
  signal?: any,
) => {
  const accessToken = store.getState().auth.accessToken;

  return {
    method: methodType,
    headers: await getHeaderObject(accessToken, strings.applicationJSON),
    body: JSON.stringify(body),
    signal,
  };
};

//Authenticated search Headers
export function getNoAuthSearchedCallParams(
  methodType: string,
  body?: any,
  signal?: any,
) {
  return {
    method: methodType,
    headers: strings.applicationJSON,
    body: body,
    signal,
  };
}

export async function getSearchedCallParams(
  methodType: string,
  body?: any,
  signal?: any,
) {
  const accessToken = store.getState().auth.accessToken;

  return {
    method: methodType,
    headers: await getHeaderObject(accessToken, strings.applicationJSON),
    body: body,
    signal,
  };
}

// Unauthenticated Call Headers
export const getNoAuthCallParams = (
  methodType: string,
  body?: any,
  signal?: any,
) => {
  return {
    method: methodType,
    headers: strings.applicationJSON,
    body: JSON.stringify(body),
    signal,
  };
};

// Embedded editor calls (embed/editor/...): authenticated by the embed session header only.
export const getEmbedCallParams = (
  methodType: string,
  body?: any,
  signal?: any,
) => {
  return {
    method: methodType,
    headers: { ...strings.applicationJSON, ...getEmbedSessionHeaders() },
    ...(body !== undefined && { body: JSON.stringify(body) }),
    signal,
  };
};

// Embedded wizard file upload: multipart body, embed session header only.
export const getEmbedFileCallParams = (
  methodType: string,
  body: any,
  signal?: any,
) => {
  return {
    method: methodType,
    headers: { ...strings.multipartForm, ...getEmbedSessionHeaders() },
    body,
    signal,
  };
};

// Authenticated Call for removing the device id without CheckId() Check in the Headers Object
export const getCallParamsWithoutCheckId = (methodType: string, body: any) => {
  const accessToken = store.getState().auth.accessToken;
  const deviceId = store.getState().auth.deviceId;
  const activeWorkspaceId = store.getState().auth.activeWorkspaceId;
  return {
    method: methodType,
    headers: {
      ...strings.applicationJSON,
      Authorization: `Bearer ${accessToken}`,
      DeviceId: deviceId,
      ...(activeWorkspaceId && !onSignerRoute()
        ? { "X-Workspace-Id": activeWorkspaceId }
        : {}),
    },
    body: JSON.stringify(body),
  };
};

export async function getAccessToken(
  methodType: string,
  body?: any,
  token?: string,
) {
  const accessToken = token!;
  return {
    method: methodType,
    headers: await getHeaderObject(accessToken, strings.applicationJSON),
    ...(isTruthy(body) && { body: JSON.stringify(body) }),
  };
}

export const getFileCallParams = async (
  method: string,
  body: any,
  signal?: any,
) => {
  const accessToken = store.getState().auth.accessToken;

  return {
    method,
    headers: await getHeaderObject(accessToken, strings.multipartForm),
    body: body,
    signal,
  };
};

// export const getFileCallParams = async (body: any) => {
//   const accessToken = "Bearer " + store.getState().auth.accessToken;
//   return {
//     method: "POST",
//     headers: await getHeaderObject(accessToken, strings.multipartForm),
//     body: body,
//   };
// };

export const getDeleteCallParams = async (body: any = {}, signal?: any) => {
  const accessToken = store.getState().auth.accessToken;
  return {
    method: "POST",
    headers: await getHeaderObject(accessToken, strings.applicationJSON),
    body: JSON.stringify(body),
    signal,
  };
};

export const getNoAuthFileCallParams = (body: any, signal?: any) => {
  return {
    method: "POST",
    headers: strings.multipartForm,
    body: body,
    signal,
  };
};

export const getAuthFileCallParams = async (
  body: FormData,
  signal?: AbortSignal,
) => {
  const accessToken = store.getState().auth.accessToken;

  return {
    method: "POST",
    headers: await getHeaderObject(accessToken, strings.multipartForm),
    body,
    signal,
  };
};

export async function getHeaderObject(accessToken: string, contentType: any) {
  try {
    // const verifiedAccessToken: string | void = await checkIdToken(
    //   accessToken,
    //   refreshToken
    // );
    const deviceId = store.getState().auth.deviceId;
    const activeWorkspaceId = store.getState().auth.activeWorkspaceId;
    if (accessToken) {
      return {
        ...contentType,
        Authorization: "Bearer " + accessToken,
        DeviceId: deviceId,
        // Sent only when a workspace is active AND not on a signer route, so existing (workspace-less)
        // and signer flows are unchanged — mirrors the backend's active-workspace-only enforcement.
        ...(activeWorkspaceId && !onSignerRoute()
          ? { "X-Workspace-Id": activeWorkspaceId }
          : {}),
      };
    } else if (IS_EMBED) {
      // No login inside a partner iframe: send the call unauthenticated (it fails with its own error)
      // rather than navigating the widget away.
      return { ...contentType };
    } else {
      history.push(viewpaths.landingViewPath);
    }
  } catch (error: any) {
    throw error;
  }
}

export interface MakeCallOptions {
  /** When true, send this API's payload and response to PostHog (SIGN_PDF, SAVE_FIELD_DATA only). */
  captureToPostHog?: boolean;
  timeoutMs?: number;
}

// If the api call takes more time than timeout, then it will timeout the call.
export const makeCall = async (
  callName: string,
  callParams: any,
  convertToJSON: boolean = true,
  options?: MakeCallOptions
) => {
  let statusCode: number | undefined;
  const captureToPostHog = options?.captureToPostHog === true;
  const startTime = captureToPostHog ? Date.now() : 0;

  const parseRequestBody = (): unknown => {
    try {
      const b = callParams?.body;
      if (b == null) return undefined;
      return typeof b === "string" ? JSON.parse(b) : b;
    } catch {
      return undefined;
    }
  };

  try {
    const blocked = getBlockingFeature(callName);
    if (blocked) throw createFeatureBlockedError(blocked);
    const permissionBlocked = getBlockingPermission(callName);
    if (permissionBlocked) throw createPermissionBlockedError(permissionBlocked);

    logApiCall(callName, callParams, "request");
    let call = fetch(callName, callParams);
    let timeout = getTimeoutPromise(options?.timeoutMs);

    const response: any = await Promise.race([timeout, call]).catch((error) => {
      throw error;
    });
    let json;
    statusCode = response.status;
    logApiCall(callName, callParams, "response", { statusCode });
    if (convertToJSON && response.status !== 204) {
      // Some endpoints (e.g. SAVE_FIELD_DATA) return an empty body on success.
      // response.json() on an empty body throws SyntaxError, so read text first
      // and only parse when there's something to parse.
      const text = await response.text();
      json = text ? JSON.parse(text) : undefined;
    }
    if (response && response.ok) {
      if (captureToPostHog) {
        captureApiToPostHog(getEventNameForEndpoint(callName), {
          url: callName,
          method: callParams?.method ?? "GET",
          requestPayload: parseRequestBody(),
          response: json,
          statusCode,
          durationMs: Date.now() - startTime,
        });
      }
      return json;
    }
    throw json;
  } catch (error: any) {
    if (captureToPostHog) {
      captureApiToPostHog(getEventNameForEndpoint(callName), {
        url: callName,
        method: callParams?.method ?? "GET",
        requestPayload: parseRequestBody(),
        response: undefined,
        statusCode,
        errorMessage: error?.message ?? error?.errorMessage ?? String(error),
        durationMs: startTime ? Date.now() - startTime : undefined,
      });
    }
    logApiCall(callName, callParams, "error", { statusCode, error });
    // The failure path above throws the parsed *body*, which drops the HTTP
    // status. Callers need it to tell 402 / 406 / 409 apart (the per-seat
    // billing flows), and a 409 body carries no `code` of its own. Expose it as
    // `statusCode` — matching makeCallFile, and deliberately not `status`,
    // which `checkStatus` reads to force a logout.
    const failure =
      error !== null && typeof error === "object"
        ? error
        : { message: error ?? notifiers.GENERIC_ERROR };
    if (statusCode !== undefined && failure.statusCode === undefined) {
      failure.statusCode = statusCode;
    }
    const isLoggedOut = checkStatus(failure);
    if (isLoggedOut) {
      failure.message = notifiers.LOGGEDOUT;
      failure.errorMessage = notifiers.LOGGEDOUT;
    }
    throw failure;
  }
};

export async function makeCallText(callName: string, callParams: any) {
  try {
    const blocked = getBlockingFeature(callName);
    if (blocked) throw createFeatureBlockedError(blocked);
    const permissionBlocked = getBlockingPermission(callName);
    if (permissionBlocked) throw createPermissionBlockedError(permissionBlocked);

    logApiCall(callName, callParams, "request");
    let call = fetch(callName, callParams);
    let timeout = getTimeoutPromise();

    const response: any = await Promise.race([timeout, call]).catch((err) => {
      throw err;
    });
    logApiCall(callName, callParams, "response", { statusCode: response.status });
    const json = await response.text();
    if (response && response.ok) {
      return json;
    } else {
      throw json;
    }
  } catch (error: any) {
    logApiCall(callName, callParams, "error", { error });
    throw error;
  }
}

export const getTimeoutPromise = (timeoutMs: number = 30000) => {
  return new Promise((resolve, reject) => {
    setTimeout(
      () =>
        reject({
          error: true,
          message: "Something went wrong. Please reload the page.",
        }),
      timeoutMs,
    );
  });
};

export const fetchDocument = async (
  url: string,
  methodType: string,
  isPublic: boolean,
  bodyObject?: any,
) => {
  try {
    const requestOption = isPublic
      ? await getNoAuthCallParams(methodType, bodyObject)
      : await getCallParams(methodType, bodyObject);
    logApiCall(url, requestOption, "request");
    const response = await fetch(url, requestOption);
    logApiCall(url, requestOption, "response", { statusCode: response.status });
    if (response.ok) {
      return response.blob();
    } else {
      return response.text().then((text) => {
        const data = text && JSON.parse(text);
        const error = (data && data.errorMessage) || response.statusText;
        return Promise.reject(error);
      });
    }
  } catch (error: any) {
    logApiCall(url, undefined, "error", { error });
    throw error;
  }
};

export const makeCallFile = async (callName: string, callParams: any) => {
  let statusCode;
  try {
    const blocked = getBlockingFeature(callName);
    if (blocked) throw createFeatureBlockedError(blocked);
    const permissionBlocked = getBlockingPermission(callName);
    if (permissionBlocked) throw createPermissionBlockedError(permissionBlocked);

    logApiCall(callName, callParams, "request");
    let call = fetch(callName, callParams);
    let timeout = getTimeoutPromise();
    const response: any = await Promise.race([timeout, call]).catch((err) => {
      throw err;
    });
    statusCode = response.status;
    logApiCall(callName, callParams, "response", { statusCode });
    if (response && response.ok) {
      return response.blob();
    } else {
      throw response.blob();
    }
  } catch (error: any) {
    logApiCall(callName, callParams, "error", { statusCode, error });
    error.statusCode = statusCode;
    error.errorMessage = handleErrorMessage(error);
    throw error;
  }
};

export async function checkIdToken(accessToken: string, refreshTok: string) {
  if (accessToken) {
    const decodedIdToken: any = jwtDecode(accessToken);
    const decodedRefreshToken: any = jwtDecode(refreshTok);
    let userEmail = decodedIdToken["ss:e"];

    if (decodedIdToken["exp"] * 1000 <= Date.now()) {
      if (userEmail && decodedRefreshToken["exp"] * 1000 < Date.now()) {
        try {
          await removeDeviceId();
        } catch (error: any) {}
        store.dispatch(logOutAction({}));
        history.push(viewpaths.loginViewPath);
        throw {
          error: true,
          message:
            "You are logged out due to inactivity. Please login to continue.",
        };
      }
      try {
        const newUserIdToken = await refreshToken(refreshTok, userEmail);
        return newUserIdToken;
      } catch (error: any) {
        throw error;
      }
    }
    return accessToken;
  }
}

let inFlightRefresh: Promise<string> | null = null;

export async function refreshToken(refreshTok: string, email: string) {
  if (inFlightRefresh) return inFlightRefresh;

  inFlightRefresh = (async () => {
    try {
      const body = {
        refreshToken: refreshTok,
        email,
      };
      const callParams = getNoAuthCallParams("POST", body);
      const response: any = await makeCall(urls.REFRESHTOKEN, callParams);
      store.dispatch(
        updateAccessToken({
          accessToken: response.authToken,
        }),
      );
      store.dispatch(
        updateRefreshToken({
          refreshToken: response.refreshToken,
        }),
      );
      return response.authToken;
    } catch (error: any) {
      if (!isTruthy(error.message)) {
        throw {
          error: true,
          message: notifiers.GENERIC_ERROR,
        };
      }
      throw error;
    } finally {
      inFlightRefresh = null;
    }
  })();

  return inFlightRefresh;
}

export async function getCredentialCallParams(methodType: string, body?: any) {
  const accessToken = store.getState().auth.accessToken;
  return {
    credentials: "include",
    method: methodType,
    body: JSON.stringify(body),
    headers: await getHeaderObject(accessToken, strings.applicationJSON),
  };
}
